/**
 * An open sheet locks the page behind it (ui.tsx lockPageScroll), so the browser only scrolls what the pointer or the focus
 * is exactly on. On a computer that left most of the screen dead (owner, 2026-10-08, "all inner scrollers", worst in step by
 * step): the wheel over the dim backdrop, the side margins of the full-screen player, its header or its button row did
 * nothing, and PageDown/Space with nothing focused (the usual state after tapping a link or the empty part of a sheet) did
 * nothing. This forwards those wheel turns and keys to the sheet's own scroller. Plain DOM, so the real-browser check bundles
 * and runs this exact code.
 */

const scrolls = (e: HTMLElement) => /^(auto|scroll)$/.test(getComputedStyle(e).overflowY);
const room = (e: HTMLElement) => e.scrollHeight - e.clientHeight > 1;

/** What a sheet scrolls: the sheet itself when its content is taller (cards), else the first inner scroller (the step player's screen). */
export function sheetScroller(sheet: HTMLElement): HTMLElement {
  if (room(sheet) && scrolls(sheet)) return sheet;
  for (const e of sheet.querySelectorAll<HTMLElement>("*")) if (room(e) && scrolls(e)) return e;
  return sheet;
}

/** Can this element still move in that direction (dy > 0 is down)? */
const canMove = (e: HTMLElement, dy: number) => scrolls(e) && (dy > 0 ? e.scrollTop + e.clientHeight < e.scrollHeight - 1 : e.scrollTop > 0);

const TEXT_FIELD = "input:not([type=button]):not([type=checkbox]):not([type=radio]):not([type=submit]), textarea, select, [contenteditable=''], [contenteditable='true']";

export function installSheetScroll(scrim: HTMLElement, sheet: HTMLElement, isTop: () => boolean = () => true): () => void {
  const onWheel = (e: WheelEvent) => {
    if (!isTop() || e.ctrlKey || e.defaultPrevented || Math.abs(e.deltaY) < Math.abs(e.deltaX) || !e.deltaY) return;
    if (!scrim.contains(e.target as Node)) return; // a sheet opened from inside this one bubbles through React, not through the page
    // The pointer is already over something that scrolls this way (the card, a long list inside it): the browser does it.
    for (let n = e.target instanceof HTMLElement ? e.target : (e.target as Node).parentElement; n && n !== scrim.parentElement; n = n.parentElement) if (canMove(n, e.deltaY)) return;
    const s = sheetScroller(sheet);
    s.scrollTop += e.deltaMode === 1 ? e.deltaY * 16 : e.deltaMode === 2 ? e.deltaY * s.clientHeight : e.deltaY;
  };
  const onKey = (e: KeyboardEvent) => {
    if (!isTop() || e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
    const k = e.key; if (!["PageDown", "PageUp", "Home", "End", "ArrowDown", "ArrowUp", " "].includes(k)) return;
    const a = document.activeElement as HTMLElement | null;
    const idle = !a || a === document.body || a === document.documentElement || a === scrim || a === sheet;
    if (!idle && a!.matches(TEXT_FIELD)) return;
    const s = sheetScroller(sheet);
    if (!idle && s.contains(a)) return; // focus is inside the scroller: the browser scrolls it
    if (!idle && !(k === "PageDown" || k === "PageUp")) return; // buttons and radios keep Space and the arrows
    const page = Math.round(s.clientHeight * 0.875);
    const to = k === "PageDown" || (k === " " && !e.shiftKey) ? s.scrollTop + page : k === "PageUp" || k === " " ? s.scrollTop - page : k === "ArrowDown" ? s.scrollTop + 40 : k === "ArrowUp" ? s.scrollTop - 40 : k === "Home" ? 0 : s.scrollHeight;
    e.preventDefault(); s.scrollTop = to;
  };
  scrim.addEventListener("wheel", onWheel, { passive: true });
  document.addEventListener("keydown", onKey);
  return () => { scrim.removeEventListener("wheel", onWheel); document.removeEventListener("keydown", onKey); };
}
